import { Box } from "@mui/material";
import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash";
import { useState } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import StatusChip from "../../../../../components/StatusChip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../../components/app/provider/patient/chart";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  SeverityRange,
  useDashboardPatientQuestionaire,
} from "../../../../../queries/dashboard/use-dashboard-patient-questionaire";
import { useDashboardPatientQuestionaireStatusUpdate } from "../../../../../queries/dashboard/use-dashboard-patient-questionaire-status-update";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import AssessmentPreviewDrawer from "../../dashboard/sections/dashboard-questionnaire-review/AssementPreviewDrawer";
import SMSModal from "../../dashboard/sections/dashboard-questionnaire-review/SMSModal";
import SendPatientAssementFormsModal from "../../patient/patient-list/patient-chart-new/assessment/PatientAssementForms/SendPatientAssementFormsModal";
import NewAppointment from "../../scheduling/appointment/NewAppointment";
import {
  ASSESSMENT_ACTIONS,
  ASSESSMENT_SEVERITY_RECORD,
} from "./AssessmentConstant";
import { AssessmentFilterData } from "./AssessmentsList";

interface AssessmentSubmittedProps {
  filters: AssessmentFilterData;
  searchKey: string;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (v: GridRowSelectionModel) => void;
  onExportAssessment?: (selected: "csv" | "pdf") => void;
  onAssessmentsChange?: (assessments: any[]) => void;
  reviewStatus: "pending_review" | "reviewed"; // Controls which API call to make
}

const AssessmentSubmitted = ({
  filters,
  searchKey,
  rowSelectionModel,
  setRowSelectionModel,
  onAssessmentsChange,
  reviewStatus,
}: AssessmentSubmittedProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const showSnackbar = useSnackbar();

  // Use reviewStatus to determine the review parameter
  const isReviewed = reviewStatus === "reviewed";
  const { data: assessmentsList, isPending } = useDashboardPatientQuestionaire({
    pagination,
    review: isReviewed, // false for pending_review, true for reviewed
    xTENANTID: getXtenantId(),
    searchString: searchKey,
    severityRange: filters?.severity?.value as SeverityRange,
    submittedDate: filters.submitted_date,
    submittedBy: (filters.submitted_by as any)?.value,
  });

  // Notify parent of assessments change
  if (assessmentsList?.data?.content && onAssessmentsChange) {
    onAssessmentsChange(assessmentsList.data.content);
  }

  const [
    questionnaireDetailsDisclosure,
    smsModalDisclosure,
    newAppointmentDisclosure,
    sendAssementFormsDisclosure,
  ] = useReduceDisclosure(4);

  const queryClient = useQueryClient();

  const { mutateAsync: updateQuestionnaireStatus } =
    useDashboardPatientQuestionaireStatusUpdate();

  const onMarkReviewed = (id: string) => {
    showToastOnApiCall({
      apiCall: updateQuestionnaireStatus({
        id: id,
        status: true,
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["dashboard-patient-questionaire"],
        });
      },
    });
  };

  const onActionClick = (action: string, row: any) => {
    switch (action) {
      case "text":
        smsModalDisclosure.open(row);
        break;
      case "view_details":
        questionnaireDetailsDisclosure.open(row);
        break;
      case "schedule":
        newAppointmentDisclosure.open({
          value: row?.patientId,
          label: row?.patientName,
        });
        break;
      case "mark_reviewed":
        onMarkReviewed(row.uuid);
        break;
      default:
        break;
    }
  };

  // Filter actions based on review status
  const availableActions = isReviewed
    ? ASSESSMENT_ACTIONS.filter((action) => action.value !== "mark_reviewed")
    : ASSESSMENT_ACTIONS;

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 70,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "patientName",
      flex: 0.4,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Patient Name",
      renderCell: ({ row }) => (
        <PatientName
          name={row?.patientName}
          gender={row?.patientGender}
          patientId={row.patientId!}
          birthDate={row?.patientDob}
        />
      ),
    },
    {
      field: "submittedBy",
      headerName: "Submitted By",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) => capitalize(row?.submittedBy) || "",
    },
    {
      field: "providerName",
      headerName: "Provider",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) => {
        return (
          <Flex gap={"10px"} sx={{}}>
            <Box>
              <Text
                isTabletext
                fontWeight={"medium"}
                marginBottom={"1px"}
                color="primary.main"
              >
                {row?.providerName ?? ""}
              </Text>
              {row?.providerRole && (
                <Text isTabletext marginBottom={"1px"}>
                  {row?.providerRole ?? ""}
                </Text>
              )}
            </Box>
          </Flex>
        );
      },
    },
    {
      field: "actionDate",
      headerName: "Submitted On",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) =>
        row.actionDate ? formatDateTime(row.actionDate) || "" : "",
    },
    {
      field: "submittedOn",
      headerName: "Reviewed On",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) =>
        row?.submittedOn ? formatDateTime(row?.submittedOn) : "",
    },
    {
      field: "severityRange",
      headerName: "Severity",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) =>
        row?.severityRange && ASSESSMENT_SEVERITY_RECORD[row.severityRange] ? (
          <StatusChip
            label={ASSESSMENT_SEVERITY_RECORD[row.severityRange]}
            variant={SeverityVariantMap[row.severityRange] ?? "default"}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "screenerType",
      headerName: "Assessment Type",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {row?.screenerType
            ? replaceUnderScoreAddSpace(row?.screenerType)
            : "-"}
        </Text>
      ),
    },
    {
      field: "score",
      headerName: "Score",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }) => (
        <Text
          isTabletext
        >{`${row.score ?? ""} / ${row.totalScore ?? ""}`}</Text>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      width: 160,
      renderCell: () =>
        isReviewed ? (
          <StatusChip
            label={isReviewed ? "Reviewed" : "Pending"}
            variant={isReviewed ? "success" : "pending"}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "Action",
      headerName: "Action",
      width: 160,
      sortable: false,
      renderCell: ({ row }: any) => (
        <>
          <StyledButtonV2
            onClick={() => onActionClick("view_details", row)}
            variant="text"
            label="View Details"
          />
          <ActionMenu
            options={availableActions}
            onActionClick={(v: string) => {
              onActionClick(v, row);
            }}
          />
        </>
      ),
    },
  ]);

  return (
    <>
      <AssessmentPreviewDrawer
        disclosureHost={questionnaireDetailsDisclosure}
        questionnaire={questionnaireDetailsDisclosure?.data}
        allowReview={!isReviewed}
        enableAssignForReview={!isReviewed}
        enableSchedule
        enablePrint
      />
      <SendPatientAssementFormsModal
        disclosureHost={sendAssementFormsDisclosure}
        showPatientSelection={true}
      />
      <SMSModal
        disclosureHost={smsModalDisclosure}
        patientUuid={smsModalDisclosure?.data?.patientId}
        patientName={smsModalDisclosure?.data?.patientName}
        patientMRN={smsModalDisclosure?.data?.patientMrn}
      />
      <NewAppointment
        prefillPatientData={newAppointmentDisclosure?.data}
        opened={newAppointmentDisclosure.opened}
        onClose={newAppointmentDisclosure.close}
      />

      {/* <GlobalTableHeightWrapper
        height="calc(100vh -  185px)"
        sx={{ overflowY: "auto" }}
      > */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        loading={isPending}
        rowSelectionModel={rowSelectionModel}
        keepNonExistentRowsSelected
        onRowSelectionModelChange={(rowSelected) => {
          setRowSelectionModel(rowSelected);
        }}
        rows={assessmentsList?.data?.content ?? []}
        rowCount={assessmentsList?.data?.page?.totalElements ?? 10}
        rowHeight={BILLING_TABLE_ROW_HEIGHT}
        columns={
          !isReviewed
            ? COLUMNS.filter((column) => column.field !== "reviewAt")
            : (COLUMNS as any)
        }
        disableRowSelectionOnClick
        checkboxSelection
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        sortModel={gridSorting}
      />
      {/* </GlobalTableHeightWrapper> */}
    </>
  );
};

export default AssessmentSubmitted;

const SeverityVariantMap: Record<
  string,
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
> = {
  VERY_LOW: "success",
  LOW: "success",
  MODERATE: "warning",
  HIGH: "error",
  VERY_HIGH: "error",
};
